import type { ProviderQuote, ProviderRatePackage } from './rates';

export type PackageSize = {
  length: number;
  width: number;
  height: number;
  pounds: number;
  ounces: number;
};

export type PackageBox = PackageSize & {
  /** The quote numbers its packages, and the rates carry that number back. */
  id: number;
};

function cleanMeasurement(value?: number) {
  if (value == null || value <= 0) {
    return undefined;
  }

  return Number(value.toFixed(2));
}

function toNumber(value: unknown): number {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : 0;
}

/** Ounces over a pound belong in the pounds, which is how the carriers read it. */
function normalizeSize(size: PackageSize): PackageSize {
  const ounces = Math.max(0, size.ounces);
  const carried = Math.floor(ounces / 16);

  return {
    length: Math.max(0, size.length),
    width: Math.max(0, size.width),
    height: Math.max(0, size.height),
    pounds: Math.max(0, size.pounds) + carried,
    ounces: Number((ounces % 16).toFixed(2)),
  };
}

/**
 * The boxes the order will ship in, as the warehouse worked them out. Every rate
 * in a quote carries the size of the package it prices, so one rate per package
 * is all it takes to read them.
 */
export function quotedBoxes(quote?: ProviderQuote): PackageBox[] {
  const byPackage = new Map<number, ProviderRatePackage>();

  Object.values(quote?.packages || {}).forEach(rates => {
    (rates || []).forEach(rate => {
      const id = rate.package_id || 1;

      if (!byPackage.has(id)) {
        byPackage.set(id, rate);
      }
    });
  });

  return [...byPackage.entries()]
    .sort(([left], [right]) => left - right)
    .map(([id, rate]) => ({
      id,
      ...normalizeSize({
        length: toNumber(rate.length),
        width: toNumber(rate.width),
        height: toNumber(rate.height),
        pounds: toNumber(rate.pounds),
        ounces: toNumber(rate.ounces),
      }),
    }));
}

/**
 * Boxes read off the item's own package attributes, used when the warehouse quote
 * comes back without a size. A box with no size is not a box.
 */
export function attributeBoxes(
  packages?: Array<Partial<PackageSize> & { id?: number }>,
): PackageBox[] {
  return (packages || [])
    .map((pkg, index) => ({
      id: pkg.id || index + 1,
      ...normalizeSize({
        length: toNumber(pkg.length),
        width: toNumber(pkg.width),
        height: toNumber(pkg.height),
        pounds: toNumber(pkg.pounds),
        ounces: toNumber(pkg.ounces),
      }),
    }))
    .filter(
      box =>
        box.length > 0 &&
        box.width > 0 &&
        box.height > 0 &&
        (box.pounds > 0 || box.ounces > 0),
    );
}

export function formatPackageSize(size: Partial<PackageSize>) {
  const dimensions = [
    cleanMeasurement(size.length),
    cleanMeasurement(size.width),
    cleanMeasurement(size.height),
  ];

  return dimensions.every(value => value != null)
    ? `${dimensions.join(' × ')} in`
    : undefined;
}

export function formatPackageWeight(size: Partial<PackageSize>) {
  const pounds = cleanMeasurement(size.pounds);
  const ounces = cleanMeasurement(size.ounces);
  const parts = [
    pounds ? `${pounds} lb` : undefined,
    ounces ? `${ounces} oz` : undefined,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(' ') : undefined;
}

export function packageSizeText(box: PackageSize): string {
  return formatPackageSize(box) || '—';
}

export function packageWeightText(box: PackageSize): string {
  return formatPackageWeight(box) || '—';
}
